/**
 * @fileoverview Session data model with JSDoc types
 *
 * Part of Phase 1: Data Boundary Refactoring
 * @version 1.0.0
 */

import { normalizeIdeas } from './idea.js';

/**
 * @typedef {Object} SessionBrief
 * @property {string} product - Product name
 * @property {string} benefit - Key benefit
 * @property {string[]} formats - Selected formats
 * @property {string|null} productId - Product UUID
 * @property {Object|null} productData - Full product data
 * @property {Object|null} brand - Brand data
 */

/**
 * @typedef {Object} SessionStats
 * @property {number} totalIdeas - Total ideas generated
 * @property {number} savedIdeas - Number of saved ideas
 * @property {number} refinedIdeas - Number of refined ideas
 * @property {number} imagesGenerated - Number of images generated
 * @property {number} videosGenerated - Number of videos generated
 * @property {number} creditsUsed - Credits consumed
 */

/**
 * @typedef {Object} Session
 * @property {string} id - UUID
 * @property {string} userId - Owner user UUID
 * @property {string|null} brandId - Brand UUID used for generation
 * @property {string} name - Session name (auto-generated or custom)
 * @property {SessionBrief} brief - Input brief
 * @property {string[]} formats - Selected formats
 * @property {string[]} triggers - Selected trigger codes
 * @property {import('./idea.js').Idea[]} ideas - Generated ideas
 * @property {SessionStats} stats - Generation statistics
 * @property {string} status - Session status (active, completed, etc.)
 * @property {string} promptVersion - Prompt version used
 * @property {string} provider - AI provider
 * @property {string} model - Model used
 * @property {string} createdAt - ISO timestamp
 * @property {string|null} updatedAt - ISO timestamp
 * @property {string|null} completedAt - ISO timestamp
 */

/**
 * Creates a normalized Session object from raw API response
 * @param {Object} raw - Raw API response
 * @returns {Session|null}
 */
export function normalizeSession(raw) {
  if (!raw) return null;

  // Handle brief - might be string or object
  let brief = raw.brief;
  if (typeof brief === 'string') {
    try {
      brief = JSON.parse(brief);
    } catch {
      brief = { product: brief, benefit: '' };
    }
  }

  // Normalize formats
  const formats = normalizeArrayField(raw.formats);

  // Normalize triggers
  const triggers = normalizeArrayField(raw.triggers || raw.trigger_codes);

  // Normalize nested ideas
  const ideas = normalizeIdeas(raw.ideas || []);

  return {
    id: raw.id,
    userId: raw.user_id || raw.userId || null,
    brandId: raw.brand_id || raw.brandId || null,
    name: raw.name || `Session ${new Date(raw.created_at || raw.createdAt || Date.now()).toLocaleDateString()}`,

    brief: {
      product: brief?.product || brief?.productName || '',
      benefit: brief?.benefit || brief?.keyBenefit || '',
      formats: brief?.formats || formats,
      productId: brief?.productId || raw.product_id || null,
      productData: brief?.productData || null,
      brand: brief?.brand || null
    },

    formats: formats,
    triggers: triggers,

    // Normalized ideas
    ideas: ideas,

    stats: {
      totalIdeas: raw.stats?.totalIdeas || raw.stats?.total_ideas || raw.ideas_count || ideas.length || 0,
      savedIdeas: raw.stats?.savedIdeas || raw.stats?.saved_ideas || ideas.filter(i => i.saved).length || 0,
      refinedIdeas: raw.stats?.refinedIdeas || raw.stats?.refined_ideas || ideas.filter(i => i.isRefined).length || 0,
      imagesGenerated: raw.stats?.imagesGenerated || raw.stats?.images_generated || ideas.filter(i => i.imageUrl).length || 0,
      videosGenerated: raw.stats?.videosGenerated || raw.stats?.videos_generated || ideas.filter(i => i.videoUrl).length || 0,
      creditsUsed: raw.stats?.creditsUsed || raw.credits_used || 0
    },

    // Session metadata
    status: raw.status || 'completed',
    promptVersion: raw.prompt_version || raw.promptVersion || null,
    provider: raw.provider || null,
    model: raw.model || null,
    meta: raw.meta || {},

    // Timestamps
    createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
    updatedAt: raw.updated_at || raw.updatedAt || null,
    completedAt: raw.completed_at || raw.completedAt || null
  };
}

/**
 * Normalize array field that might be string or array
 * @param {string|string[]} field
 * @returns {string[]}
 */
function normalizeArrayField(field) {
  if (!field) return [];
  if (typeof field === 'string') {
    try {
      field = JSON.parse(field);
    } catch {
      field = [field];
    }
  }
  return Array.isArray(field) ? field : [];
}

/**
 * Normalize array of sessions
 * @param {Object[]} rawSessions
 * @returns {Session[]}
 */
export function normalizeSessions(rawSessions) {
  if (!Array.isArray(rawSessions)) return [];
  return rawSessions.map(normalizeSession).filter(Boolean);
}

/**
 * Convert normalized Session back to database format
 * @param {Session} session
 * @returns {Object}
 */
export function sessionToDb(session) {
  if (!session) return null;

  return {
    id: session.id,
    brand_id: session.brandId || null,
    name: session.name,
    brief: session.brief,
    formats: session.formats,
    triggers: session.triggers,
    status: session.status,
    prompt_version: session.promptVersion,
    provider: session.provider,
    model: session.model,
    meta: session.meta
  };
}
